import * as React from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Braces,
  CheckCircle2,
  Eye,
  History,
  Loader2,
  Save,
  Send,
} from "lucide-react";
import {
  Badge,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { SettingsBreadcrumbs } from "@/components/AppBreadcrumbs";
import { useTemplate, useUpdateTemplate } from "@/lib/emailTemplates/api";
import { LivePreviewPane } from "@/components/email-templates/LivePreviewPane";
import { EmailEditor, type EmailEditorValue } from "@/components/email-templates/EmailEditor";
import { TestSendDialog } from "@/components/email-templates/TestSendDialog";
import { VariablesSidebar } from "@/components/email-templates/VariablesSidebar";
import { VersionHistoryDialog } from "@/components/email-templates/VersionHistoryDialog";
import type { EmailDesignJson } from "@/lib/emailTemplates/types";

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function EmailTemplateEditorPage() {
  const { key = "", slug } = useParams<{ key: string; slug?: string }>();
  const { user } = useAuth();
  const tenantSlug = slug ?? null;

  const template = useTemplate(key, tenantSlug);
  const update = useUpdateTemplate(key, tenantSlug);

  const [subject, setSubject] = React.useState("");
  const [htmlBody, setHtmlBody] = React.useState("");
  const [textBody, setTextBody] = React.useState("");
  const [designJson, setDesignJson] = React.useState<EmailDesignJson | null>(null);
  const [testSendOpen, setTestSendOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [variablesOpen, setVariablesOpen] = React.useState(false);
  const [savedFlash, setSavedFlash] = React.useState(false);

  const insertSubjectRef = React.useRef<HTMLInputElement | null>(null);
  const editorRef = React.useRef<{ exportNow: () => Promise<EmailEditorValue> } | null>(null);

  // Initial buffer hydration when the template loads.
  React.useEffect(() => {
    if (template.data) {
      setSubject(template.data.subject);
      setHtmlBody(template.data.html_body);
      setTextBody(template.data.text_body);
      setDesignJson(template.data.design_json);
    }
  }, [template.data]);

  const insertAtFocused = (snippet: string) => {
    // The visual editor has its own merge-tag picker, so the only
    // place we still need cursor insertion is the subject field.
    const el = insertSubjectRef.current;
    if (!el) return;
    const start = el.selectionStart ?? subject.length;
    const end = el.selectionEnd ?? subject.length;
    const next = subject.slice(0, start) + snippet + subject.slice(end);
    setSubject(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const onEditorChange = React.useCallback((next: EmailEditorValue) => {
    setHtmlBody(next.html_body);
    setTextBody(next.text_body);
    setDesignJson(next.design_json);
  }, []);

  const isDirty =
    template.data &&
    (subject !== template.data.subject ||
      htmlBody !== template.data.html_body ||
      textBody !== template.data.text_body ||
      JSON.stringify(designJson) !== JSON.stringify(template.data.design_json));

  const onSave = async () => {
    try {
      // Pull the freshest values out of the editor (Visual mode
      // debounces; Code mode passes through). This guarantees the
      // payload we save matches what the SA sees on screen at the
      // moment they hit Save.
      const fresh = (await editorRef.current?.exportNow()) ?? {
        html_body: htmlBody,
        text_body: textBody,
        design_json: designJson,
      };
      await update.mutateAsync({
        subject,
        html_body: fresh.html_body,
        text_body: fresh.text_body,
        design_json: fresh.design_json,
      });
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1400);
    } catch {
      // Error rendered below from update.error
    }
  };

  if (template.isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="text-muted-foreground size-5 animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (template.isError || !template.data) {
    return (
      <div className="grid gap-2 py-12 text-center">
        <p className="text-destructive text-sm">
          {(template.error as Error)?.message ?? "Could not load template."}
        </p>
        <Link to="/settings/email-templates" className="text-sm underline">
          Back to templates
        </Link>
      </div>
    );
  }

  const t = template.data;
  const updateError =
    update.error instanceof ApiError ? update.error.message : update.error?.message;

  return (
    <div className="grid gap-5">
      {/*
        Phase B2.7 redesign: full-width body editor with Preview promoted
        to a Button → Modal and Variables to a collapsible right Sheet.
        The previous 3-column layout pinched the editor below 600px wide
        on most laptops; the SA's primary task is editing, not glancing.
      */}
      <PageHeader>
        <PageHeaderTop>
          <SettingsBreadcrumbs>
            <BreadcrumbItem className="hidden sm:inline-flex">
              <BreadcrumbLink asChild>
                <Link to="/settings/email-templates">Email templates</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="hidden sm:inline-flex" />
            <BreadcrumbItem>
              <BreadcrumbPage>{t.spec.display_name}</BreadcrumbPage>
            </BreadcrumbItem>
          </SettingsBreadcrumbs>
          <PageHeaderActions>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVariablesOpen(true)}
              aria-label="Open variables panel"
            >
              <Braces className="size-4" aria-hidden="true" />
              Variables
              <Badge variant="secondary" className="ml-1 text-[10px]">
                {t.spec.variables.length}
              </Badge>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              aria-label="Open live preview"
            >
              <Eye className="size-4" aria-hidden="true" />
              Preview
            </Button>
            <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
              <History className="size-4" aria-hidden="true" />
              History
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTestSendOpen(true)}
              disabled={!user?.email}
            >
              <Send className="size-4" aria-hidden="true" />
              Send test to me
            </Button>
            <Button size="sm" onClick={onSave} disabled={!isDirty || update.isPending}>
              {update.isPending ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Saving…
                </>
              ) : savedFlash ? (
                <>
                  <CheckCircle2 className="size-4" aria-hidden="true" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="size-4" aria-hidden="true" />
                  Save changes
                </>
              )}
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>{t.spec.display_name}</PageHeaderTitle>
          <PageHeaderDescription>
            {tenantSlug ? (
              <>
                Per-tenant override for <strong>{tenantSlug}</strong>
                {" · "}
              </>
            ) : (
              "Global template · "
            )}
            <span>v{t.version || 0}</span>
            {t.version > 0 && (
              <>
                {" "}
                · last edited {formatDate(t.updated_at)} by {t.updated_by_email}
              </>
            )}
            {" · "}
            <Badge variant={t.source === "bundled_file" ? "warn" : "success"}>
              {t.source.replace("_", " ")}
            </Badge>
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      <Button variant="ghost" size="sm" className="-ml-2 w-fit" asChild>
        <Link
          to="/settings/email-templates"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Back to templates
        </Link>
      </Button>

      {updateError && (
        <div
          role="alert"
          className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{updateError}</span>
        </div>
      )}

      {/*
        Subject + Body stack vertically. The Body card claims the full
        width of <main> so the visual editor / Monaco gets the room it
        needs (the previous layout was pinning it to ~520px on a 1440
        screen, which made HTML edits painful).
      */}
      <div className="grid gap-1.5">
        <Label
          htmlFor="email-subject"
          className="text-muted-foreground text-xs uppercase tracking-wide"
        >
          Subject
        </Label>
        <Input
          id="email-subject"
          ref={insertSubjectRef}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Activate your {{ tenant_name }} admin account"
          className="font-mono text-sm"
        />
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Body</CardTitle>
        </CardHeader>
        <CardContent>
          <EmailEditor
            ref={editorRef}
            initial={{
              html_body: t.html_body,
              text_body: t.text_body,
              design_json: t.design_json,
            }}
            onChange={onEditorChange}
            variables={t.spec.variables}
            brandPrimary={null}
            legacy={t.design_json === null && (t.html_body?.length ?? 0) > 0}
          />
        </CardContent>
      </Card>

      {/*
        Preview lives in a Dialog so the SA opts in to seeing rendered
        output. The pane internally debounces; mounting/unmounting is
        intentional — closing the modal cancels in-flight previews.
      */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Live preview</DialogTitle>
            <DialogDescription>
              Server-rendered with sample variable values. Updates ~400ms after each keystroke.
            </DialogDescription>
          </DialogHeader>
          {previewOpen && (
            <LivePreviewPane
              templateKey={key}
              subject={subject}
              html_body={htmlBody}
              text_body={textBody}
            />
          )}
        </DialogContent>
      </Dialog>

      {/*
        Variables panel as a right-side Sheet. Click-to-insert still
        targets the focused subject field — the editor's own merge-tag
        picker handles cursor insertion inside Monaco / the visual
        canvas, so this Sheet's onInsert is subject-only.
      */}
      <Sheet open={variablesOpen} onOpenChange={setVariablesOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-sm">
          <SheetHeader>
            <SheetTitle>Available variables</SheetTitle>
            <SheetDescription>
              Click to insert at the cursor of the focused subject field. The body editor has its
              own merge-tag picker.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <VariablesSidebar variables={t.spec.variables} onInsert={insertAtFocused} />
          </div>
        </SheetContent>
      </Sheet>

      <TestSendDialog
        open={testSendOpen}
        onOpenChange={setTestSendOpen}
        templateKey={key}
        subject={subject}
        html_body={htmlBody}
        text_body={textBody}
        callerEmail={user?.email ?? "your address"}
      />
      <VersionHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        templateKey={key}
        tenantSlug={tenantSlug}
        onRestored={() => template.refetch()}
      />
    </div>
  );
}
